import { ArrowLeft, RefreshCw } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { OTPInput } from "../../components/Inputs";
import { useAuth } from "../../context/AuthProvider";
import { resendOtpApi } from "../../api/authAPI";
import { toast } from "react-toastify";
import logger from "../../utils/logger";
import {
  formatOtpCountdown,
  getOtpCooldownSeconds,
  getOtpRetryAfterSeconds,
} from "../../utils/otpCooldown";

const VerifyOTP = () => {
  const { otpVerify, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const initialTimer = getOtpCooldownSeconds(location.state);
  const [otp, setOtp] = useState<string>("");
  const [isResending, setIsResending] = useState(false);
  const [timer, setTimer] = useState(initialTimer);
  const [canResend, setCanResend] = useState(initialTimer <= 0);
  const [error, setError] = useState("");

  const type = location?.state?.type;
  const dialCode = location?.state?.dialCode;
  const phone = location?.state?.phone;
  const email = location?.state?.email;
  const redirectTo = location?.state?.redirectTo;

  useEffect(() => {
    if (timer > 0) {
      const countdown = setTimeout(() => setTimer(timer - 1), 1000);
      return () => clearTimeout(countdown);
    } else {
      setCanResend(true);
    }
  }, [timer]);

  const validateOtp = (value: string) => {
    if (value.length !== 6) {
      return "Please enter a 6-digit OTP";
    }
    if (!/^\d{6}$/.test(value)) {
      return "OTP must contain only numbers";
    }
    return null;
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const otpError = validateOtp(otp);
    if (otpError) {
      setError(otpError);
      return;
    }
    if (phone && otp) {
      await otpVerify({ phone, otp, purpose: type, redirectTo });
    }
  };

  const handleResend = async () => {
    setIsResending(true);

    try {
      let res = await resendOtpApi({ phone: phone || "", purpose: type });
      console.log("res", res);
      let data = res?.data;
      if (data?.status) {
        setTimer(getOtpCooldownSeconds(data));
        setCanResend(getOtpCooldownSeconds(data) <= 0);
        toast.success(data?.message || "OTP resent successfully");
      } else {
        const retryAfterSeconds = getOtpRetryAfterSeconds(data);
        if (retryAfterSeconds) {
          setTimer(retryAfterSeconds);
          setCanResend(false);
        }
        toast.error(data?.message || "Error resending OTP");
      }
    } catch (error: any) {
      const data = error?.response?.data;
      const retryAfterSeconds = getOtpRetryAfterSeconds(data);
      if (retryAfterSeconds) {
        setTimer(retryAfterSeconds);
        setCanResend(false);
      }

      toast.error(
        error?.message ||
          error?.response?.data?.message ||
          "Error resending OTP",
      );
      logger.error("Error resending OTP:", error);
    } finally {
      setIsResending(false);
    }
  };

  const getTitle = () => {
    switch (type) {
      case "signup":
        return "Verify your account";
      case "signin":
        return "Two-factor authentication";
      case "forgot-password":
        return "Verify your identity";
      default:
        return "Enter verification code";
    }
  };

  const getDescription = () => {
    const destination =
      dialCode +
        "-" +
        phone?.substring(0, 2) +
        "XXXXXX" +
        phone?.substring(8) ||
      email?.substring(0, 2) + "XXXXXX" + email?.substring(email.length - 2);
    switch (type) {
      case "signup":
        return `We've sent a 6-digit code to ${destination}. Enter it below to verify your account.`;
      case "signin":
        return `Enter the 6-digit code sent to ${destination} to complete your sign in.`;
      case "forgot-password":
        return `Enter the 6-digit code sent to ${destination} to reset your password.`;
      default:
        return `Enter the 6-digit verification code sent to ${destination}.`;
    }
  };

  useEffect(() => {
    if (otp.length === 6) {
      handleSubmit(
        new Event("submit") as unknown as React.FormEvent<HTMLFormElement>,
      );
    }
  }, [otp]);
  return (
    <div className="w-full max-w-md mx-auto">
      <div className="text-center mb-8">
        <div className="bg-white backdrop-blur-sm rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
          {/* <CheckCircle2 className="w-8 h-8 text-white" /> */}
          <img src="logo.png" alt="NyayNet Logo" className="w-10 h-10 " />
        </div>
        <h1 className="text-3xl font-bold text-white mb-2">{getTitle()}</h1>
        <p className="text-blue-100">{getDescription()}</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-blue-100 mb-4 text-center">
            Enter 6-Digit Code
          </label>
          <OTPInput
            value={otp}
            length={6}
            onChange={(val) => {
              setOtp(val);
            }}
            autoFocus={true}
            className="w-full text-white"
            disabled={loading}
          />
          {error && (
            <p className="text-red-500 text-sm mt-2 text-center">{error}</p>
          )}

          {/* <div className="flex justify-center space-x-2">
            {otpValues.map((value, index) => (
              <input
                key={index}
                id={`otp-${index}`}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={value}
                onChange={(e) => handleOtpChange(index, e.target.value)}
                onKeyDown={(e) => handleOtpKeyDown(index, e)}
                className="w-12 h-12 text-center text-xl font-semibold bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all"
              />
            ))}
          </div> */}
        </div>

        <div className="text-center">
          {!canResend ? (
            <p className="text-blue-100 text-sm mb-4">
              Didn't receive the code? Resend in {formatOtpCountdown(timer)}
            </p>
          ) : (
            <button
              type="button"
              onClick={handleResend}
              disabled={isResending}
              className="text-white  hover:text-blue-200 transition-colors text-sm font-medium"
            >
              {isResending ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Resending...
                </>
              ) : (
                "Resend OTP"
              )}
            </button>
          )}
        </div>

        <button
          type="submit"
          // onClick={handleSubmit}
          disabled={loading || otp.length < 6}
          className="w-full bg-white text-slate-800 font-semibold py-3 px-6 rounded-lg hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-blue-600 transition-all transform hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
        >
          Verify Otp
        </button>

        <div className="text-center">
          <button
            type="button"
            onClick={() => {
              navigate(-1);
            }}
            className="inline-flex items-center text-blue-100 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </button>
        </div>
      </form>
    </div>
  );
};

export default VerifyOTP;
